import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import StructuredData from '../../../components/seo/StructuredData';

interface Court {
  id: string;
  name: string;
  district: string;
  location: string;
  rating: number;
  total_reviews: number;
  amenities: string[];
  operating_hours: {
    weekdays: string;
    weekends: string;
  };
  photo_url?: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  contact_phone?: string;
  contact_email?: string;
}

const CourtDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [court, setCourt] = useState<Court | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchCourtDetails();
    }
  }, [id]);

  useEffect(() => {
    if (court) {
      // Update page title and meta description
      document.title = `${court.name} - Pickleball Court in ${court.district} | HK PicklePlay`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Book ${court.name} in ${court.district}, Hong Kong. Professional pickleball court with ${court.amenities?.join(', ')}. Rating: ${court.rating}/5 from ${court.total_reviews} reviews. View details, amenities, and book your session.`);
      }

      // Update canonical URL
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', window.location.origin + `/courts/${court.id}`);
      }
    }
  }, [court]);

  const fetchCourtDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Force fresh data from database, bypass any cache
      const { data, error } = await supabase
        .from('courts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        setError('Court not found');
        return;
      }

      console.log('Fetched court data:', data);
      setCourt(data);
    } catch (error) {
      console.error('Error fetching court details:', error);
      setError('Failed to load court details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookSession = () => {
    try {
      navigate('/sessions');
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  // Get main court image
  const getCourtImage = (court: Court) => {
    if (court.photo_url) {
      return court.photo_url;
    }
    
    return `https://public.readdy.ai/ai/img_res/74771e4295297088475e84f95d11f0ee.jpg`;
  };

  const reviews = [
    {
      id: 1,
      user: 'Sarah Chen',
      rating: 5,
      date: '2024-01-15',
      comment:
        'Excellent courts with great lighting. The surface is well-maintained and the facilities are clean. Highly recommend for serious players!',
    },
    {
      id: 2,
      user: 'Mike Wong',
      rating: 4,
      date: '2024-01-10',
      comment:
        'Good location and decent facilities. Parking can be challenging during peak hours but overall a solid choice.',
    },
    {
      id: 3,
      user: 'Lisa Park',
      rating: 5,
      date: '2024-01-08',
      comment:
        'Love playing here! The courts are always in great condition and the staff is friendly. The pro shop has everything you need.',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading court details...</p>
        </div>
      </div>
    );
  }

  if (error || !court) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error || 'Court not found'}</p>
          <button 
            onClick={() => navigate('/courts')}
            className="mt-4 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
          >
            Back to Courts
          </button>
        </div>
      </div>
    );
  }

  const courtImage = getCourtImage(court);

  return (
    <>
      {/* Add structured data for the court */}
      <StructuredData 
        type="SportsActivityLocation" 
        data={{
          id: court.id,
          name: court.name,
          description: court.description || `Professional pickleball court in ${court.district}, Hong Kong. Features ${court.amenities?.join(', ')}.`,
          address: court.address || court.location,
          district: court.district,
          phone: court.contact_phone || court.phone,
          amenities: court.amenities,
          rating: court.rating,
          total_reviews: court.total_reviews,
          operating_hours: court.operating_hours
        }} 
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Image Gallery */}
        <header className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Main Image */}
              <div className="space-y-4">
                <div className="relative h-96 rounded-lg overflow-hidden">
                  <img
                    src={courtImage}
                    alt={`${court.name} - Professional pickleball court in ${court.district}, Hong Kong`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Court Info */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{court.name}</h1>
                  <div className="flex items-center mb-4">
                    <div className="flex items-center mr-4">
                      <i className="ri-star-fill text-yellow-400 mr-1"></i>
                      <span className="font-medium">{court.rating}</span>
                      <span className="text-gray-600 ml-1">({court.total_reviews} reviews)</span>
                    </div>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm">
                      {court.district}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {court.description || `Premier pickleball facility located in ${court.district}. Our courts feature professional-grade surfaces, excellent lighting for evening play, and convenient amenities. Perfect for players of all skill levels.`}
                  </p>
                </div>

                {/* Quick Info */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Quick Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-gray-400 mr-3 w-5 mt-1"></i>
                      <div>
                        <div className="text-gray-700 font-medium">{court.location}</div>
                        {court.address && (
                          <div className="text-gray-600 text-sm mt-1">{court.address}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-phone-line text-gray-400 mr-3 w-5"></i>
                      <span className="text-gray-700">{court.contact_phone || court.phone || '+852 2234 5678'}</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line text-gray-400 mr-3 w-5"></i>
                      <span className="text-gray-700">{court.contact_email || court.email || 'info@' + court.name.toLowerCase().replace(/\s+/g, '') + '.hk'}</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-time-line text-gray-400 mr-3 w-5"></i>
                      <div>
                        <div className="text-gray-700">Weekdays: {court.operating_hours.weekdays}</div>
                        <div className="text-gray-700">Weekends: {court.operating_hours.weekends}</div>
                      </div>
                    </div>
                    {court.website && (
                      <div className="flex items-center">
                        <i className="ri-global-line text-gray-400 mr-3 w-5"></i>
                        <a href={court.website} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <button
                    onClick={handleBookSession}
                    className="flex-1 bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Book a Session
                  </button>
                  <button className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                    Add to Favorites
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs Section */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: 'ri-information-line' },
                  { id: 'amenities', label: 'Amenities', icon: 'ri-service-line' },
                  { id: 'reviews', label: 'Reviews', icon: 'ri-star-line' },
                  { id: 'location', label: 'Location', icon: 'ri-map-pin-line' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <section className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">About This Court</h3>
                    <p className="text-gray-700 leading-relaxed">
                      {court.description || `Located in the ${court.district} district, our facility offers state-of-the-art courts with professional-grade surfaces that meet international standards. Whether you're a beginner looking to learn or an advanced player seeking competitive play, our courts provide the perfect environment for all skill levels.`}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium mb-3">Court Features</h4>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Professional-grade court surface with optimal ball bounce</li>
                      <li>Regulation-size courts meeting USA Pickleball standards</li>
                      <li>High-quality LED lighting for evening play</li>
                      <li>Weather-resistant court materials</li>
                      <li>Spectator seating areas</li>
                    </ul>
                  </div>
                </section>
              )}

              {activeTab === 'amenities' && (
                <section>
                  <h3 className="text-xl font-semibold mb-6">Available Amenities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {court.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg">
                        <i className="ri-check-line text-teal-600 mr-3"></i>
                        <span className="text-gray-700">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {activeTab === 'reviews' && (
                <section>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                    <h3 className="text-xl font-semibold">Reviews</h3>
                    <button className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                      Write a Review
                    </button>
                  </div>
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <article key={review.id} className="border-b border-gray-200 pb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                              <span className="text-teal-600 font-medium text-sm">
                                {review.user
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{review.user}</h4>
                              <div className="flex items-center flex-wrap">
                                {[...Array(5)].map((_, i) => (
                                  <i
                                    key={i}
                                    className={`ri-star-${i < review.rating ? 'fill' : 'line'} text-yellow-400 text-sm`}
                                  ></i>
                                ))}
                                <span className="text-gray-500 text-xs sm:text-sm ml-2">{review.date}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm sm:text-base leading-relaxed break-words">{review.comment}</p>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              {activeTab === 'location' && (
                <section>
                  <h3 className="text-xl font-semibold mb-6">Location &amp; Directions</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    <div className="order-2 lg:order-1">
                      <div className="bg-gray-100 rounded-lg h-48 sm:h-64 overflow-hidden">
                        <iframe
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent('114.1,22.2,114.3,22.4')}&layer=mapnik&marker=${encodeURIComponent('22.3,114.2')}`}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          className="rounded-lg"
                          title={`Map showing location of ${court.name} in ${court.district}, Hong Kong`}
                        ></iframe>
                      </div>
                      <div className="mt-2 text-center">
                        <a 
                          href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(court.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:text-teal-700 text-xs sm:text-sm break-all"
                        >
                          <i className="ri-external-link-line mr-1"></i>
                          View on OpenStreetMap
                        </a>
                      </div>
                    </div>
                    <div className="space-y-4 order-1 lg:order-2">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Address</h4>
                        <p className="text-gray-700 text-sm sm:text-base break-words leading-relaxed">{court.address || court.location}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Public Transport</h4>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-subway-line mr-2 text-blue-600 mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs sm:text-sm break-words">Nearest MTR Station (5-10 min walk)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-bus-line mr-2 text-green-600 mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs sm:text-sm break-words">Multiple bus routes available</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Parking</h4>
                        <p className="text-gray-700 text-xs sm:text-sm break-words leading-relaxed">Parking available. Please check with venue for rates.</p>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CourtDetail;
